import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { getUserData } from "../../../services/apiService";
import {
  askFormBuilderAssist,
  fetchFaqAiStatus,
} from "../../../services/faqService";
import { ESourceAccessGate, useESourceAccess } from "./esourceAccess";
import {
  applyFormBuilderServerAssist,
  generateESourceFormFromPrompt,
} from "./esourceAiAssist";
import { formStudioReplyFromAssistResult, matchKnownStudioFormTemplate } from "./esourceFormStudioAssist";
import {
  fetchTemplateById,
  saveTemplate,
} from "./esourceService";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildEsourceFormStudioTourSteps } from "./esourceFormStudioTourSteps";
import {
  hasCompletedEsourceFormStudioTour,
  markEsourceFormStudioTourCompleted,
} from "./esourceFormStudioTourStorage";
import {
  buildFormFromDocumentPrompt,
  extractAssistDocumentText,
} from "./esourceAssistDocumentImport";
import "./ESourcePages.scss";
import "./ESourceFormStudio.scss";

const FORM_STUDIO_ENGINE = "formStudio";
const FORM_STUDIO_SCHEMA_VERSION = 1;
const FORM_STUDIO_SRC = `${process.env.PUBLIC_URL || ""}/form-studio/index.html?v=sampleSubmit2`;
const OTHER_FORM_TYPE = "Other";

/** True when the user clearly asked to add one (or a few) fields — not build a whole form. */
function isMinimalFieldAddRequest(prompt) {
  const t = String(prompt || "").trim();
  if (!t || t.length > 160) return false;
  if (!/\b(add|insert|include)\b/i.test(t)) return false;
  if (
    /\b(create|build|generate|design|pack|vital|adverse|receipt|dispense|whole form|complete form)\b/i.test(
      t,
    )
  ) {
    return false;
  }
  if (/,|;|\/|\band\b.+\band\b/i.test(t)) return false;
  return (
    /\badd\s+(?:a\s+|an\s+|the\s+|one\s+)?(?:new\s+)?field\b/i.test(t) ||
    /\badd\s+(?:a\s+|an\s+|the\s+|one\s+)?(?:new\s+)?[\w\s-]{1,40}\s+field\b/i.test(
      t,
    ) ||
    /\badd\s+(?:a\s+|an\s+|the\s+|one\s+)?(?:new\s+)?(e-?mail|emailid|email\s*id|phone|mobile|comments?|notes?)\b/i.test(
      t,
    )
  );
}

function studioFieldsToClassic(existingFields = []) {
  return (Array.isArray(existingFields) ? existingFields : []).map((f, i) => ({
    id: `fs-${i}-${String(f.key || f.type || "f")}`,
    type:
      f.type === "short"
        ? "text"
        : f.type === "long"
          ? "textarea"
          : f.type === "consent"
            ? "checkbox"
            : f.type === "checkboxes"
              ? "multi_select"
              : f.type === "calc"
                ? "calculated"
                : f.type || "text",
    label: f.label || f.key || "Field",
    name: f.key || `field_${i + 1}`,
    fieldName: f.key || `field_${i + 1}`,
    required: !!f.required,
    options: f.options || [],
  }));
}

function postToStudio(iframe, message) {
  try {
    iframe?.contentWindow?.postMessage(
      { source: "aq-form-studio-host", ...message },
      "*",
    );
  } catch {
    /* ignore */
  }
}

export function isFormStudioSchema(schemaJson) {
  try {
    const parsed =
      typeof schemaJson === "string"
        ? JSON.parse(schemaJson || "{}")
        : schemaJson || {};
    return (
      parsed?.engine === FORM_STUDIO_ENGINE ||
      parsed?.Engine === FORM_STUDIO_ENGINE
    );
  } catch {
    return false;
  }
}

export function parseFormStudioDocument(schemaJson) {
  try {
    const parsed =
      typeof schemaJson === "string"
        ? JSON.parse(schemaJson || "{}")
        : schemaJson || {};
    return parsed.document || parsed.Document || null;
  } catch {
    return null;
  }
}

export function buildFormStudioSchemaJson(document) {
  return JSON.stringify({
    engine: FORM_STUDIO_ENGINE,
    version: FORM_STUDIO_SCHEMA_VERSION,
    document,
  });
}

/** GetTemplateById returns SchemaJson on version rows, not the template header. */
function sortVersionsDesc(versions = []) {
  return [...versions].sort(
    (a, b) => Number(b.VersionNo || 0) - Number(a.VersionNo || 0),
  );
}

function pickEditableVersion(versions = []) {
  const list = sortVersionsDesc(versions);
  const draft = list.find((v) => /draft/i.test(String(v.Status || "")));
  if (draft) return draft;
  return (
    list.find((v) => /published/i.test(String(v.Status || ""))) ||
    list[0] ||
    null
  );
}

function schemaJsonFromTemplateResponse(template, versions = []) {
  const fromTemplate =
    template?.SchemaJson ||
    template?.schemaJson ||
    template?.DraftSchemaJson ||
    template?.draftSchemaJson ||
    "";
  if (fromTemplate) return String(fromTemplate);
  const version = pickEditableVersion(versions);
  return String(
    version?.SchemaJson ||
      version?.schemaJson ||
      version?.DraftSchemaJson ||
      version?.draftSchemaJson ||
      "",
  );
}

/**
 * Form Studio builder — embeds the free-form Word-style builder 1:1
 * inside AQTrials chrome (header / sidebar / footer stay from Layout).
 * AI uses AQ Assist (AskFormBuilderAi), not Claude artifact APIs.
 */
export default function ESourceFormStudio() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { loading: accessLoading, canDesign } = useESourceAccess();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { showLoader, hideLoader } = useLoader();

  const notify = useCallback(
    (title, message, type = "info") => {
      showAlert({ title, message, type });
    },
    [showAlert],
  );

  const iframeRef = useRef(null);
  const aiAbortRef = useRef(null);
  const [studioReady, setStudioReady] = useState(false);
  const [formTemplateId, setFormTemplateId] = useState(isNew ? "" : id);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [moduleScope, setModuleScope] = useState(OTHER_FORM_TYPE);
  const [busy, setBusy] = useState(false);
  const [pendingDoc, setPendingDoc] = useState(null);
  const [assistStatus, setAssistStatus] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCode, setSaveCode] = useState("");
  const [showFormStudioTour, setShowFormStudioTour] = useState(false);
  const [frameNonce, setFrameNonce] = useState(0);

  const frameSrc = useMemo(
    () => `${FORM_STUDIO_SRC}${frameNonce ? `?r=${frameNonce}` : ""}`,
    [frameNonce],
  );

  const user = useMemo(() => getUserData() || {}, []);
  const assistOrgId = String(user.OrganizationId || user.organizationId || "");
  const assistStaffId = String(user.StaffId || user.staffId || "");

  const formStudioTourSteps = useMemo(
    () => buildEsourceFormStudioTourSteps({ isNew }),
    [isNew],
  );

  useEffect(() => {
    if (!assistStaffId) return undefined;
    if (hasCompletedEsourceFormStudioTour(assistStaffId)) return undefined;
    const timer = window.setTimeout(() => setShowFormStudioTour(true), 1000);
    return () => window.clearTimeout(timer);
  }, [assistStaffId]);

  const getStudioHost = useCallback(() => {
    try {
      return iframeRef.current?.contentWindow?.AQ_FORM_STUDIO_HOST || null;
    } catch {
      return null;
    }
  }, []);

  const pushDocumentToStudio = useCallback(
    (doc) => {
      const host = getStudioHost();
      if (!host || !doc) return false;
      host.setDocument(doc);
      return true;
    },
    [getStudioHost],
  );

  const loadStudioBlank = useCallback(() => {
    const host = getStudioHost();
    if (!host || typeof host.resetBlank !== "function") return false;
    return !!host.resetBlank();
  }, [getStudioHost]);

  const handleAiRequest = useCallback(
    async (requestId, payload = {}) => {
      const mode = "add";
      let userPrompt = String(payload.userPrompt || payload.prompt || "").trim();
      const iframe = iframeRef.current;

      const fail = (message, code = "assist_unavailable") => {
        postToStudio(iframe, {
          type: "ai-error",
          requestId,
          message,
          code,
        });
      };

      const dataUrlToFile = async (dataUrl, fileName, fallbackType) => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const type = blob.type || fallbackType || "application/octet-stream";
        return new File([blob], fileName || "attachment", { type });
      };

      const mimeForAttachedDocument = (fileName, attachKind) => {
        const n = String(fileName || "").toLowerCase();
        if (attachKind === "pdf" || n.endsWith(".pdf")) return "application/pdf";
        if (n.endsWith(".docx") || attachKind === "word") {
          if (n.endsWith(".doc") && !n.endsWith(".docx")) {
            return "application/msword";
          }
          if (n.endsWith(".docx")) {
            return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
          }
          return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        }
        if (n.endsWith(".doc")) return "application/msword";
        return "application/octet-stream";
      };

      const isDocumentAttach =
        !!payload.documentDataUrl ||
        payload.attachKind === "pdf" ||
        payload.attachKind === "word" ||
        payload.attachKind === "doc" ||
        payload.attachKind === "docx" ||
        /\.(pdf|docx?)$/i.test(String(payload.fileName || ""));

      try {
        if (isDocumentAttach) {
          const fileName = payload.fileName || "form.pdf";
          const file = await dataUrlToFile(
            payload.documentDataUrl || payload.imageDataUrl,
            fileName,
            mimeForAttachedDocument(fileName, payload.attachKind),
          );
          const extracted = await extractAssistDocumentText(file);
          userPrompt = buildFormFromDocumentPrompt({
            ...extracted,
            userNote: userPrompt,
          });
        }
      } catch (err) {
        fail(
          err?.message ||
            "Could not read that file. Please upload a PDF, Word document, or image.",
          "image_rejected",
        );
        return;
      }

      if (!userPrompt && !payload.imageDataUrl) {
        fail("Describe what you want to add first.", "empty_completion");
        return;
      }

      postToStudio(iframe, {
        type: "ai-status",
        text: "Asking AQ Assist…",
        cls: "busy",
      });

      try {
        let status = assistStatus;
        if (!status) {
          status = await fetchFaqAiStatus(assistOrgId, assistStaffId);
          setAssistStatus(status);
        }
        if (!status?.formBuilderEnabled) {
          fail(
            "AQ Assist Form Builder AI is not enabled for your organisation.",
            "assist_unavailable",
          );
          return;
        }

        const allowedModes =
          Array.isArray(status.formBuilderModes) && status.formBuilderModes.length
            ? status.formBuilderModes
            : ["clinicalPacks"];

        const classicFields = studioFieldsToClassic(payload.existingFields);
        const fromDocument =
          !!payload.documentDataUrl ||
          payload.attachKind === "pdf" ||
          payload.attachKind === "word" ||
          payload.attachKind === "doc" ||
          payload.attachKind === "docx" ||
          /\.(pdf|docx?)$/i.test(String(payload.fileName || ""));
        const question = [
          userPrompt,
          payload.lang && payload.lang !== "auto"
            ? `Language: ${payload.lang}.`
            : "",
          "Add ONLY the field(s) the user asked for to the existing form.",
          "Do not invent extra fields, sections, or a whole form. Prefer a single operations add.",
          "Do not remove or replace current fields unless the user asked to.",
          payload.imageDataUrl && !fromDocument
            ? "A form screenshot was attached in the builder; recreate its fields from the text description if image data is not available server-side."
            : "",
        ]
          .filter(Boolean)
          .join(" ");

        const abort = new AbortController();
        aiAbortRef.current = abort;

        let serverResult = null;
        if (!fromDocument) {
          serverResult = await askFormBuilderAssist({
            organizationId: assistOrgId,
            staffId: assistStaffId,
            question: question.slice(0, 900),
            chatSessionId:
              String(payload.chatSessionId || "").trim() ||
              `form-studio-${formTemplateId || "new"}`,
            formName: payload.title || name || "Form Studio",
            formHeader: payload.title || name || "",
            canvasSnapshot: classicFields.map((f, i) => ({
              index: i + 1,
              type: f.type,
              label: f.label,
              fieldName: f.fieldName,
              required: f.required,
            })),
            allowedModes,
          });
          if (serverResult) {
            setAssistStatus((prev) =>
              prev
                ? {
                    ...prev,
                    dailyOrgCap: serverResult.dailyOrgCap ?? prev.dailyOrgCap,
                    dailyUserCap: serverResult.dailyUserCap ?? prev.dailyUserCap,
                    orgUsedToday: serverResult.orgUsedToday ?? prev.orgUsedToday,
                    userUsedToday:
                      serverResult.userUsedToday ?? prev.userUsedToday,
                    orgRemaining: serverResult.orgRemaining ?? prev.orgRemaining,
                    userRemaining:
                      serverResult.userRemaining ?? prev.userRemaining,
                  }
                : prev,
            );
          }
        }

        if (abort.signal.aborted) {
          fail("Stopped. Nothing was changed.", "cancelled");
          return;
        }

        postToStudio(iframe, {
          type: "ai-status",
          text: "Building fields…",
          cls: "busy",
        });

        let assistResult;
        if (serverResult?.blocked) {
          fail(serverResult.summary || "AQ Assist blocked this request.", "refused");
          return;
        }

        if (fromDocument || serverResult?.useLocal) {
          assistResult = generateESourceFormFromPrompt(userPrompt, {
            allowedModes,
            currentFields: classicFields,
            formName: payload.title || name,
            formHeader: payload.title || name,
          });
        } else {
          const applied = applyFormBuilderServerAssist(
            serverResult,
            classicFields,
          );
          if (
            !applied?.blocked &&
            Array.isArray(applied?.fields) &&
            applied.fields.length
          ) {
            assistResult = applied;
          } else {
            assistResult = generateESourceFormFromPrompt(userPrompt, {
              allowedModes,
              currentFields: classicFields,
              formName: payload.title || name,
              formHeader: payload.title || name,
            });
          }
        }

        // Guard: "add email" must not dump a whole pack onto Form Studio.
        if (
          isMinimalFieldAddRequest(userPrompt) &&
          Array.isArray(assistResult?.fields) &&
          assistResult.fields.length > classicFields.length + 3
        ) {
          const local = generateESourceFormFromPrompt(userPrompt, {
            allowedModes,
            currentFields: classicFields,
            formName: payload.title || name,
            formHeader: payload.title || name,
          });
          if (
            !local?.blocked &&
            Array.isArray(local?.fields) &&
            local.fields.length
          ) {
            assistResult = local;
          }
        }

        if (assistResult?.blocked) {
          fail(assistResult.summary || "AQ Assist could not build that form.", "refused");
          return;
        }

        const summaryText =
          assistResult?.summary ||
          serverResult?.summary ||
          "AQ Assist updated the form.";

        // Known client forms (CP05A / SM02A / CP03A / CP07B) → same layout as reference Word packs.
        const knownReply = matchKnownStudioFormTemplate(userPrompt, {
          title: assistResult?.name || payload.title || name,
          name: assistResult?.name || payload.title || name,
          fileName: payload.fileName,
        });
        if (knownReply && (fromDocument || !classicFields.length)) {
          postToStudio(iframe, {
            type: "ai-result",
            requestId,
            payload: knownReply,
          });
          return;
        }
        // Chat create / rebuild of a known form code or title (even if canvas has sample fields).
        if (
          knownReply &&
          /\b(create|build|make|generate|rebuild|replace)\b/i.test(userPrompt) &&
          /\b(form|log|check|cp05a|cp03a|cp07b|sm02a|accountability|temperature|biological|compliance)\b/i.test(
            userPrompt,
          )
        ) {
          postToStudio(iframe, {
            type: "ai-result",
            requestId,
            payload: knownReply,
          });
          return;
        }

        const hasFieldPayload =
          Array.isArray(assistResult?.fields) && assistResult.fields.length > 0;

        // Chat-only / no canvas change (e.g. advice, or incremental edit with no adds).
        if (!hasFieldPayload) {
          if (String(summaryText || "").trim()) {
            postToStudio(iframe, {
              type: "ai-result",
              requestId,
              payload: {
                sections: [],
                summary: summaryText,
                applied: false,
              },
            });
            return;
          }
          fail(
            "AQ Assist returned no fields. Try a more specific request.",
            "empty_completion",
          );
          return;
        }

        const replaceFromDocument =
          fromDocument ||
          (!!assistResult?.replaceCanvas && !assistResult?.incremental);

        const reply = formStudioReplyFromAssistResult(
          {
            ...assistResult,
            name: assistResult.name || payload.title || name,
            summary: summaryText,
          },
          mode,
          {
            currentFields: replaceFromDocument ? [] : classicFields,
            replace: replaceFromDocument,
          },
        );

        // Incremental Assist returned the full canvas but nothing new to append.
        if (!reply?.sections?.some((s) => s?.items?.length)) {
          postToStudio(iframe, {
            type: "ai-result",
            requestId,
            payload: {
              sections: [],
              summary: reply?.summary || summaryText,
              applied: false,
            },
          });
          return;
        }

        postToStudio(iframe, {
          type: "ai-result",
          requestId,
          payload: reply,
        });
      } catch (err) {
        fail(
          String(err?.message || "Could not reach AQ Assist. Please try again."),
          "assist_unavailable",
        );
      } finally {
        aiAbortRef.current = null;
      }
    },
    [
      assistStatus,
      assistOrgId,
      assistStaffId,
      formTemplateId,
      name,
    ],
  );

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.source !== "aq-form-studio") return;
      if (data.type === "ready" || data.type === "ai-ready") {
        setStudioReady(true);
      }
      if (data.type === "ai-abort") {
        try {
          aiAbortRef.current?.abort();
        } catch {
          /* ignore */
        }
      }
      if (data.type === "ai-request" && data.requestId) {
        handleAiRequest(data.requestId, data.payload || {});
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleAiRequest]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchFaqAiStatus(assistOrgId, assistStaffId);
        if (!cancelled) setAssistStatus(status);
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assistOrgId, assistStaffId]);

  useEffect(() => {
    if (!studioReady || !pendingDoc) return;
    pushDocumentToStudio(pendingDoc);
    setPendingDoc(null);
  }, [studioReady, pendingDoc, pushDocumentToStudio]);

  // New form: start blank (sample available via Form Studio "Untitled starter").
  useEffect(() => {
    if (!studioReady || !isNew) return;
    loadStudioBlank();
  }, [studioReady, isNew, loadStudioBlank]);

  useEffect(() => {
    if (!studioReady || !assistStatus) return;
    postToStudio(iframeRef.current, {
      type: "ai-config",
      enabled: !!assistStatus.formBuilderEnabled,
      orgId: assistOrgId,
      staffId: assistStaffId,
      usage: {
        used: Math.max(0, Number(assistStatus.userUsedToday) || 0),
        cap: Math.max(1, Number(assistStatus.dailyUserCap) || 50),
      },
      caps: {
        images: {
          mediaTypes: ["image/jpeg", "image/png", "image/webp"],
          maxInputBytes: 20 * 1048576,
        },
      },
    });
  }, [studioReady, assistStatus, assistOrgId, assistStaffId]);

  useEffect(() => {
    if (isNew || !id) return undefined;
    let cancelled = false;
    (async () => {
      showLoader();
      try {
        const res = await fetchTemplateById(id);
        if (cancelled) return;
        if (!res.success || !res.template) {
          notify(
            "Open form",
            res.message || "Could not load this form template.",
            "danger",
          );
          navigate("/esource/templates");
          return;
        }
        const t = res.template;
        setFormTemplateId(String(t.FormTemplateId || id));
        setName(String(t.Name || t.name || "").trim());
        setCode(String(t.Code || t.code || "").trim());
        setModuleScope(
          String(t.ModuleScope || t.moduleScope || OTHER_FORM_TYPE).trim() ||
            OTHER_FORM_TYPE,
        );
        const schema = schemaJsonFromTemplateResponse(t, res.versions || []);
        if (isFormStudioSchema(schema)) {
          const doc = parseFormStudioDocument(schema);
          if (doc) setPendingDoc(doc);
          else {
            notify(
              "Open form",
              "This Form Studio template has no document to edit.",
              "warning",
            );
          }
        } else if (schema) {
          notify(
            "Classic form",
            "This template was built with the classic field builder. Opening the classic editor.",
            "info",
          );
          navigate(`/esource/templates/${id}?classic=1`, { replace: true });
        } else {
          notify(
            "Open form",
            "No saved schema was found for this template.",
            "warning",
          );
        }
      } finally {
        if (!cancelled) hideLoader();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, notify, showLoader, hideLoader]);

  const loadTemplateIntoStudio = useCallback(async () => {
    if (isNew || !id) {
      setStudioReady(false);
      setPendingDoc(null);
      setFrameNonce((n) => n + 1);
      return;
    }
    showLoader();
    try {
      const res = await fetchTemplateById(id);
      if (!res.success || !res.template) {
        notify(
          "Refresh",
          res.message || "Could not reload this form template.",
          "danger",
        );
        return;
      }
      const t = res.template;
      setFormTemplateId(String(t.FormTemplateId || id));
      setName(String(t.Name || t.name || "").trim());
      setCode(String(t.Code || t.code || "").trim());
      setModuleScope(
        String(t.ModuleScope || t.moduleScope || OTHER_FORM_TYPE).trim() ||
          OTHER_FORM_TYPE,
      );
      const schema = schemaJsonFromTemplateResponse(t, res.versions || []);
      if (isFormStudioSchema(schema)) {
        const doc = parseFormStudioDocument(schema);
        if (doc) {
          if (studioReady && pushDocumentToStudio(doc)) {
            setPendingDoc(null);
          } else {
            setStudioReady(false);
            setPendingDoc(doc);
            setFrameNonce((n) => n + 1);
          }
          notify("Refresh", "Form reloaded from the server.", "success");
          return;
        }
      }
      notify(
        "Refresh",
        "No Form Studio document found for this template.",
        "warning",
      );
    } finally {
      hideLoader();
    }
  }, [
    isNew,
    id,
    studioReady,
    pushDocumentToStudio,
    notify,
    showLoader,
    hideLoader,
  ]);

  const openSaveModal = useCallback(() => {
    const host = getStudioHost();
    let doc = null;
    try {
      doc = host?.getDocument?.() || null;
    } catch {
      doc = null;
    }
    if (!host || !doc) {
      notify(
        "Save form",
        !studioReady
          ? "Form Studio is still loading. Try again in a moment."
          : "Could not read the canvas. Click Refresh, then try Save form again.",
        "warning",
      );
      return;
    }
    const docTitle = String(doc.title || name || "").trim();
    setSaveName(docTitle || name || "");
    setSaveCode(String(code || "").trim());
    setSaveModalOpen(true);
  }, [getStudioHost, name, code, notify, studioReady]);

  const closeSaveModal = useCallback(() => {
    setSaveModalOpen(false);
  }, []);

  const onSave = useCallback(
    async ({ nameValue = name, codeValue = code } = {}) => {
      const host = getStudioHost();
      const doc = host?.getDocument?.();
      if (!doc) {
        notify(
          "Save form",
          "Form Studio is still loading. Try again in a moment.",
          "warning",
        );
        return;
      }
      const nextName = String(nameValue || "").trim();
      const nextCode = String(codeValue || "").trim();
      if (!nextName || !nextCode) {
        notify("Validation", "Code and name are required.", "warning");
        return;
      }
      const nextDoc = { ...doc, title: nextName };
      setBusy(true);
      showLoader();
      try {
        const res = await saveTemplate({
          FormTemplateId: formTemplateId || null,
          Name: nextName,
          Code: nextCode,
          ModuleScope: moduleScope || OTHER_FORM_TYPE,
          SchemaJson: buildFormStudioSchemaJson(nextDoc),
        });
        if (!res.success) {
          notify("Save form", res.message || "Save failed.", "danger");
          return;
        }
        try {
          host.setDocument?.(nextDoc);
        } catch {
          /* ignore */
        }
        const newId = String(res.formTemplateId || formTemplateId || "").trim();
        setName(nextName);
        setCode(nextCode);
        if (newId && newId !== String(formTemplateId || "")) {
          setFormTemplateId(newId);
          navigate(`/esource/templates/${newId}`, { replace: true });
        }
        notify(
          isNew ? "Save" : "Update",
          isNew
            ? "Form has been saved successfully"
            : "Form has been updated successfully",
          "success",
        );
      } finally {
        setBusy(false);
        hideLoader();
      }
    },
    [
      getStudioHost,
      name,
      code,
      moduleScope,
      formTemplateId,
      isNew,
      navigate,
      notify,
      showLoader,
      hideLoader,
    ],
  );

  const confirmSaveModal = useCallback(async () => {
    const nextName = String(saveName || "").trim();
    const nextCode = String(saveCode || "").trim();
    if (!nextName || !nextCode) {
      notify("Validation", "Code and name are required.", "warning");
      return;
    }
    closeSaveModal();
    await onSave({ nameValue: nextName, codeValue: nextCode });
  }, [saveName, saveCode, closeSaveModal, onSave, notify]);

  return (
    <ESourceAccessGate allow={canDesign} loading={accessLoading}>
      <div className="esource-page esource-form-studio-page">
        <PageBreadcrumb
          items={[
            { label: "eSource", path: "/esource/templates" },
            { label: "Form Builder" },
          ]}
          toolbarExtras={
            <button
              type="button"
              className="actions-trigger"
              data-tour="esource-fs-refresh"
              onClick={() => {
                void loadTemplateIntoStudio();
              }}
              disabled={busy}
              title="Refresh form"
            >
              <i className="fas fa-sync-alt me-1" aria-hidden="true" />
              Refresh
            </button>
          }
          trailingExtras={
            <div className="esource-builder-crumb-actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm esource-tour-help-btn"
                data-tour="esource-fs-guide"
                onClick={() => setShowFormStudioTour(true)}
                title="Show page guide"
              >
                <i className="fas fa-route me-1" aria-hidden="true" />
                Guide
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                data-tour="esource-fs-save"
                onClick={openSaveModal}
                disabled={busy || !studioReady}
                title={isNew ? "Save draft" : "Update form"}
              >
                <i className="fas fa-save me-1" aria-hidden="true" />
                {busy ? "Saving…" : isNew ? "Save form" : "Update"}
              </button>
            </div>
          }
        />
        <div
          className="esource-form-studio-frame-wrap"
          data-tour="esource-fs-canvas"
        >
          {!studioReady ? (
            <div className="esource-form-studio-loading" role="status">
              Loading Form Studio…
            </div>
          ) : null}
          <iframe
            ref={iframeRef}
            title="Form Studio"
            className="esource-form-studio-frame"
            src={frameSrc}
            allow="clipboard-read; clipboard-write"
          />
        </div>

        {saveModalOpen ? (
          <div
            className="modal fade show d-block esource-builder-save-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="esource-fs-save-modal-title"
            style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="esource-fs-save-modal-title">
                    {isNew ? "Save draft" : "Update form"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeSaveModal}
                  />
                </div>
                <div className="modal-body">
                  <p className="esource-builder-save-modal__hint">
                    Enter the form code and name before{" "}
                    {isNew ? "saving" : "updating"}.
                  </p>
                  <div className="esource-builder-save-modal__field">
                    <label htmlFor="esource-fs-save-code">
                      Code <span className="mandatory">*</span>
                    </label>
                    <input
                      id="esource-fs-save-code"
                      className="form-control form-control-sm"
                      value={saveCode}
                      disabled={!isNew && !!formTemplateId}
                      onChange={(e) => setSaveCode(e.target.value)}
                      placeholder="e.g. CP05A"
                      autoFocus={isNew || !formTemplateId}
                    />
                  </div>
                  <div className="esource-builder-save-modal__field">
                    <label htmlFor="esource-fs-save-name">
                      Name <span className="mandatory">*</span>
                    </label>
                    <input
                      id="esource-fs-save-name"
                      className="form-control form-control-sm"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Form display name"
                      autoFocus={!isNew && !!formTemplateId}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={closeSaveModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => {
                      void confirmSaveModal();
                    }}
                  >
                    {isNew ? "Save draft" : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <IsfPageTour
          open={showFormStudioTour}
          steps={formStudioTourSteps}
          staffId={assistStaffId}
          markTourCompleted={markEsourceFormStudioTourCompleted}
          onClose={() => setShowFormStudioTour(false)}
        />

        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    </ESourceAccessGate>
  );
}
